import React from "react";
import { useLocation } from "react-router-dom";

/**
 * Crash containment for the admin dashboard.
 *
 * React unmounts the whole tree when a component throws during render, so
 * before this existed a single malformed record could blank the entire admin
 * app. Unlike the public site — where each page renders its own navbar — the
 * dashboard has real shared chrome, so placing a boundary inside <main> keeps
 * the sidebar and top nav alive and the admin can simply click to another
 * page. That is the whole point: contain the failure to the panel that failed.
 *
 * Deliberately dependency-free with inline styles. A fallback that relies on
 * the same styling pipeline that may have just crashed is not a fallback.
 */

interface Props {
  children: React.ReactNode;
  /** Change this to clear a caught error — pass the pathname so navigation recovers. */
  resetKey?: string | number;
  /** Fills its container rather than the viewport. Use inside shared chrome. */
  compact?: boolean;
}

interface State {
  error: Error | null;
}

const isDev = process.env.NODE_ENV === "development";

function reportCrash(error: unknown, info?: string) {
  // eslint-disable-next-line no-console
  console.error("[crash]", error, info || "");
  // TODO(observability): forward to an error reporter when one is wired up.
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    reportCrash(error, info?.componentStack || undefined);
  }

  componentDidUpdate(prevProps: Props) {
    if (
      this.state.error &&
      prevProps.resetKey !== undefined &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      this.setState({ error: null });
    }
  }

  private handleRetry = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: this.props.compact ? "60vh" : "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px 16px",
          fontFamily: "Inter, 'Helvetica Neue', Arial, sans-serif",
        }}
      >
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E0E0E0",
            borderRadius: 12,
            padding: 32,
            maxWidth: 520,
            width: "100%",
            textAlign: "center",
          }}
        >
          <h1
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: "#1A1A1A",
              margin: "0 0 8px",
            }}
          >
            Something went wrong
          </h1>
          <p
            style={{
              fontSize: 14,
              color: "#707070",
              margin: "0 0 24px",
              lineHeight: 1.6,
            }}
          >
            This panel hit an unexpected problem. The rest of the dashboard is
            still working — try again, or pick another page from the sidebar.
          </p>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={this.handleRetry}
              style={{
                background: "#16729A",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "10px 20px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              TRY AGAIN
            </button>
            <button
              type="button"
              onClick={() => {
                window.location.href = "/";
              }}
              style={{
                background: "transparent",
                color: "#16729A",
                border: "1.5px solid #16729A",
                borderRadius: 8,
                padding: "10px 20px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              GO TO DASHBOARD
            </button>
          </div>

          {isDev ? (
            <pre
              style={{
                marginTop: 24,
                padding: 12,
                background: "#F0F0F0",
                borderRadius: 6,
                fontSize: 11,
                color: "#B51D28",
                textAlign: "left",
                overflowX: "auto",
                whiteSpace: "pre-wrap",
              }}
            >
              {String(error.stack || error.message || error)}
            </pre>
          ) : null}
        </div>
      </div>
    );
  }
}

/**
 * Resets the boundary whenever the route changes, so navigating away from a
 * crashed page clears the error instead of stranding the admin on the
 * fallback card.
 *
 * Uses useLocation rather than window.location + popstate: react-router links
 * navigate via pushState, which does NOT fire popstate, so a listener-based
 * version would only reset on browser back/forward and leave the admin stuck
 * when they clicked the sidebar.
 *
 * Must be rendered inside <BrowserRouter>.
 */
export const RouteErrorBoundary: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary resetKey={pathname} compact>
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;
